import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AiOutlineArrowLeft } from 'react-icons/ai';
import { useAuth } from '../../context/AuthContext';
import { getMySessionsApi, terminateSessionApi, terminateOtherSessionsApi } from '../../redux/apis/sessionApis';
import successToast from '../../components/toast/successToast';
import errorToast from '../../components/toast/errorToast';
import moment from 'moment';
import Loading from '../../components/ui/loader/loader';

const Profile = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [terminatingId, setTerminatingId] = useState(null);
    const [terminatingOthers, setTerminatingOthers] = useState(false);

    const fetchSessions = useCallback(async () => {
        setLoadingSessions(true);
        try {
            const res = await getMySessionsApi();
            if (res?.data?.data) setSessions(res.data.data);
        } catch (_) {
            errorToast('Failed to load sessions');
        } finally {
            setLoadingSessions(false);
        }
    }, []);

    useEffect(() => { fetchSessions(); }, [fetchSessions]);

    const handleTerminate = async (sessionId) => {
        setTerminatingId(sessionId);
        try {
            const res = await terminateSessionApi(sessionId);
            if (res?.data?.statusCode === 200) {
                successToast(res.data.message || 'Session terminated!');
                fetchSessions();
            } else {
                errorToast(res?.data?.message || 'Failed to terminate session');
            }
        } catch (_) {
            errorToast('Failed to terminate session');
        } finally {
            setTerminatingId(null);
        }
    };

    const handleTerminateOthers = async () => {
        setTerminatingOthers(true);
        try {
            const res = await terminateOtherSessionsApi();
            if (res?.data?.statusCode === 200) {
                successToast(res.data.message || 'All other sessions terminated!');
                fetchSessions();
            } else {
                errorToast(res?.data?.message || 'Failed to terminate sessions');
            }
        } catch (_) {
            errorToast('Failed to terminate sessions');
        } finally {
            setTerminatingOthers(false);
        }
    };

    const otherSessionsCount = sessions.filter(s => !s.is_current).length;

    return (
        <div className="w-100 d-flex flex-column" style={{ height: '100%' }}>
            <div
                className="d-flex align-items-center border-bottom px-3 position-sticky top-0 bg-white"
                style={{ height: 53, flexShrink: 0 }}
            >
                <AiOutlineArrowLeft
                    className="cursor_pointer me-2"
                    style={{ color: '#4A81E0', fontSize: 20 }}
                    onClick={() => navigate(-1)}
                />
                <span className="fs_18 fw_500">Profile</span>
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>
                <div className="p-3 p-md-4 text-start w-100">

                    {/* Profile Details */}
                    <h6 className="fw_600 fs_20 mb-3">Profile Details</h6>
                    <div className="card mb-4" style={{ overflow: 'hidden' }}>
                        <div className="card-body p-0">
                            <table className="table mb-0 user-detail-table">
                                <tbody>
                                    <tr>
                                        <th>Name</th>
                                        <td>{user?.name || '—'}</td>
                                    </tr>
                                    <tr>
                                        <th>Email</th>
                                        <td>{user?.email || '—'}</td>
                                    </tr>
                                    <tr>
                                        <th>Role</th>
                                        <td>
                                            <span className={`badge ${user?.is_admin ? 'bg-primary' : 'bg-secondary'}`}>
                                                {user?.is_admin ? 'Admin' : 'User'}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Status</th>
                                        <td>
                                            <span className={`badge ${user?.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                                                {user?.status?.toUpperCase() || '—'}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Active Sessions */}
                    <div className="d-flex align-items-center gap-3 mb-3">
                        <h6 className="fw_600 fs_20 mb-0">Active Sessions</h6>
                        {otherSessionsCount > 0 && (
                            <button
                                className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                onClick={handleTerminateOthers}
                                disabled={terminatingOthers}
                            >
                                {terminatingOthers
                                    ? <span className="spinner-border spinner-border-sm" />
                                    : <i className="bi bi-shield-x" />
                                }
                                Sign Out Other Sessions ({otherSessionsCount})
                            </button>
                        )}
                    </div>

                    <div className="card mb-4" style={{ overflow: 'hidden' }}>
                        {loadingSessions ? (
                            <div className="p-4"><Loading text="Loading sessions..." /></div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover mb-0 user-card-table">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Device</th>
                                            <th>IP Address</th>
                                            <th className="text-nowrap">Last Active</th>
                                            <th className="text-nowrap">Started</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sessions.length > 0 ? sessions.map((s) => (
                                            <tr key={s.id}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <i className="bi bi-display text-muted" />
                                                        <span>{s.device_summary || 'Unknown Device'}</span>
                                                        {s.is_current && (
                                                            <span className="badge bg-success ms-1">Current</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="text-muted">{s.ip_address || '—'}</td>
                                                <td className="text-nowrap text-muted">
                                                    {moment(s.last_active_at).fromNow()}
                                                </td>
                                                <td className="text-nowrap text-muted">
                                                    {moment(s.createdAt).format('MMM D, YYYY h:mm A')}
                                                </td>
                                                <td>
                                                    {!s.is_current && (
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleTerminate(s.id)}
                                                            disabled={terminatingId === s.id}
                                                        >
                                                            {terminatingId === s.id
                                                                ? <span className="spinner-border spinner-border-sm" />
                                                                : 'Sign Out'
                                                            }
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="5" className="text-center text-muted py-4">
                                                    No active sessions
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default Profile;
